/**
 * This module shows or hides the toolbar as the user scrolls up or down.
 *
 * @module app/components/Toolbar
 */
import jQuery from 'jquery';
import OddsFormatProvider from './OddsFormatProvider';
import XsellMicroNotification from './crossSell/signposting/XsellMicroNotification';

/**
 * Topics for MessageBus
 *
 * @type {Object}
 *
 * @private
 */
var messageBusTopics = {
    domReady: 'AppJs.domReady',
    addBet: 'addBet',
    removeBet: 'removeBet',
    internalRoute: 'internalRoute',
    betslipCount: 'wh.sports.betslip.headless.selected_selections',
};

/**
 * How many second the Acca Price popup should be visible.
 *
 * @type {number}
 * @private
 */
var accaPriceTimeout = 6;

/**
 * Timeout controlling the fadeout of the Acca Price popup.
 *
 * @type {?number}
 * @private
 */
var accaPriceTimeoutId;

/**
 * Contains the list of DOM elements to be used and their selectors
 *
 * @type {Object}
 *
 * @private
 */
var DOMElements = {
    toolbar: {
        element: null,
        selector: '#toolbar',
    },
    accuPopup: {
        element: null,
        selector: '#acca-price',
    },
    accuPopupArrow: {
        element: null,
        selector: '.acca-price__toolbar-betslip-ind',
    },
    header: {
        element: null,
        selector: '#header',
    },
    gamesLobbyButton: {
        element: null,
        selector: '.topGamesButton',
    },
    betslipButton: {
        element: null,
        selector: '#betslip-btn-toolbar',
    },
    promotionsButton: {
        element: null,
        selector: '#promos-btn-toolbar',
    },
};

/**
 * A helper method to get the element and caching its value in the DOMElements object structure
 *
 * @returns {object}
 *
 * @private
 */
var getElement = function getElement(object) {

    if (object.element === null) {

        object.element = jQuery(object.selector);
    }

    return object.element;
};

/**
 * This will make this module start listening events declared for the DOM set-up
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToMessageBus = function subscribeToMessageBus() {
    window.WH.messageBus.subscribe(messageBusTopics.addBet, updateAccaPrice);
    window.WH.messageBus.subscribe(messageBusTopics.removeBet, updateAccaPrice);
};

/**
 * Updates the Accumulator price hint on the toolbar.
 */
var updateAccaPrice = function updateAccuPrice() {

    var accumulator = getAccumulator();

    if (accumulator) {

        var popupElement = getElement(DOMElements.accuPopup);
        var arrowElement = getElement(DOMElements.accuPopupArrow);

        if (accaPriceTimeoutId) {
            clearTimeout(accaPriceTimeoutId);
        }

        accaPriceTimeoutId = setTimeout(function () {
            popupElement
                .removeClass('acca-price--fadeIn')
                .addClass('acca-price--fadeOut');
            arrowElement.removeClass('acca-price__toolbar-betslip-ind--show');
        }, accaPriceTimeout * 1000);

        arrowElement.addClass('acca-price__toolbar-betslip-ind--show');
        popupElement
            .removeClass('acca-price--fadeOut')
            .addClass('acca-price--fadeIn')
            .find('.acca-price__price').text(accumulator.odds).end()
            .find('.acca-price__title').text(accumulator.type);
    }
};

/**
 * Returns an object with the type and odds of the accumulator, if there's one with price.
 * Null if not.
 *
 * @returns {?{type: string, odds: string}}
 *
 * @private
 */
var getAccumulator = function getAccumulator() {

    var combiLength = document.betslip.combi.length;
    for (var i = 0; i < combiLength; i++) {
        var currentCombi = document.betslip.combi[i];
        if (isValidCombiToCalculateAccumulator(currentCombi)) {

            /*jshint -W106 */
            return {
                type: currentCombi.bet_type_xl,
                odds: OddsFormatProvider.convertSingle(currentCombi.price.odds, true),
            };
            /*jshint +W106 */
        }
    }

    return null;
};

var replaceBetslipWithPromo = function replaceBetslipWithPromo(data) {
    const betslipButton = getElement(DOMElements.betslipButton);
    const promotionsButton = getElement(DOMElements.promotionsButton);

    const isPromosEnabled =
        (data && data.isEnabled) &&
        (window && window.WH && window.WH.sportsbook && window.WH.sportsbook.lang === 'en-gb');

    if (isPromosEnabled) {
        betslipButton.css('display', 'none');
        promotionsButton.css('display', 'block');
    } else {
        betslipButton.css('display', 'block');
        promotionsButton.css('display', 'none');
    }
};

var initPromotionsButtonState = function initPromotionsButtonState() {
    // The Optimizely-backed promo experiment was decommissioned, so keep the toolbar in its default state.
    replaceBetslipWithPromo({ isEnabled: false });
};

/**
 * Check if the combi has the required information to be used in the
 * accumulator calculation.
 * Return True if the object has the required information, False otherwise.
 *
 * @param combi {object} The combination to be evaluated
 *
 * @returns {boolean}
 *
 * @private
 */
var isValidCombiToCalculateAccumulator = function (combi) {
    return !!(combi && combi.accumulator && combi.price && combi.price.odds);
};

/**
 * Initialise micro notification for the games lobby icon
 * @returns {void}
 * @private
 */
var initXsellMicroNotification = function initXsellMicroNotification() {
    var gamesLobbyContainer = document.getElementById('gamesLobbyContainer');
    if (gamesLobbyContainer) {
        var domMicroNotificationElement = gamesLobbyContainer.querySelector('.toolbar-xsell-micro-notification');
        var toolbarXsellMicroNotification = new XsellMicroNotification({
            container: domMicroNotificationElement,
            shouldExpire: true,
        });
        toolbarXsellMicroNotification.init();
    }};

/**
 * Binds a click event to games lobby toolbar button
 * @returns {void}
 * @private
 */
var initXsellOpenCloseHandler = function initXsellOpenCloseHandler() {
    // add onClick handler to xsell toolbar button
    var gamesLobbyHash = 'top-games';
    var button = getElement(DOMElements.gamesLobbyButton);

    button.bind('click', function () {
        if (window.WH.sportsbook.lang === 'ja-jp' ) {
            window.location = 'https://vegas.williamhill.com/ja-jp';
        } else {
            var hasGamesLobbyHash = window.location.hash.indexOf(gamesLobbyHash) > -1;
            window.location.hash = hasGamesLobbyHash ? '' : gamesLobbyHash;
        }
    });
};

var trackPromotionsLink = function trackGAEvent() {
    if (window.AnalyticsTactical) {
        window.AnalyticsTactical.Helpers.sendData(
            'link',
            window.AnalyticsTactical.Sports.localHelpers.enrichWithCommonInfo({
                NonInteractionEvent: '0',
                EventCategory: 'Test Events',
                EventAction: 'ACTE-495',
                EventLabel: 'Promo Botton Nav',
            })
        );
    }

};

var initPromotionsButtonHandler = function initPromotionsButtonHandler() {
    var url = '/betting/en-gb/apps/promotions';
    var promotionsButton = getElement(DOMElements.promotionsButton);

    promotionsButton.bind('click', function () {
        trackPromotionsLink();
        window.location.pathname = url;
    });
};

/**
 * This function initializes the communication with the native app
 *
 * @returns {void}
 *
 * @public
 */
var init = function init() {
    subscribeToMessageBus();
    initXsellMicroNotification();
    initXsellOpenCloseHandler();
    initPromotionsButtonState();
    initPromotionsButtonHandler();
};

const api = {
    init,
};

export default api;
